import { defineDocument } from './types.js'
import {
  SYNCED_ACTIONS, adjustmentsGrid, chargesGrid, currencySection, fcyColumns, pricedLines,
  termsSection,
} from './common.js'

export const purchaseOrder = defineDocument({
  key: 'purchase_order',
  group: 'Purchase and Expense',
  blurb: 'Create new orders for materials, service requests or stock. You can also view existing orders.',
  route: 'pur_ords',
  label: 'Purchase order',
  labelPlural: 'Purchase orders',
  numbering: 'PURCHASE_ORDER',
  numberPrefix: '{CO}/PO#',
  workflow: 'PURCHASE_ORDER_3_STAGE',
  tabs: ['summary', 'related', 'revisions', 'form', 'drive'],

  createWizard: {
    title: 'Create a purchase order',
    subtitle: 'Answer these three questions and the right form opens for you. You can change any of them later.',
    fields: [
      {
        key: 'orderType', label: 'What is this order for?', control: 'radio', required: true,
        lockAfterDraft: true,
        options: [
          { value: 'PROJECT', label: 'A project' },
          { value: 'STOCK', label: 'Stock' },
          { value: 'TOOL', label: 'A tool' },
        ],
      },
      { key: 'supplierId', label: 'Supplier', control: 'lookup', required: true, lookup: { entity: 'supplier' }, hint: 'Start typing to search. A blocked supplier is refused when the order is submitted.' },
      // Asked only where it means something. A stock order has no project, and
      // a project order has no warehouse until you say the goods go to one.
      { key: 'projectId', label: 'Project', control: 'lookup', required: true, lookup: { entity: 'project' },
        visibleWhen: { field: 'orderType', equals: ['PROJECT'] } },
      { key: 'receiveAt', label: 'Receive the materials at', control: 'radio',
        visibleWhen: { field: 'orderType', equals: ['PROJECT'] },
        options: [{ value: 'SITE', label: 'The site' }, { value: 'WAREHOUSE', label: 'A warehouse' }] },
      { key: 'warehouseId', label: 'Warehouse', control: 'lookup', required: true, lookup: { entity: 'warehouse' },
        visibleWhen: { field: 'orderType', equals: ['STOCK', 'TOOL'] } },
      { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', required: true, lookup: { entity: 'tax_unit' } },
    ],
  },

  form: {
    sections: [
      {
        key: 'general', n: '01', title: 'General information',
        fields: [
          { key: 'orderType', label: 'Order type', control: 'select', required: true, width: 'half', lockAfterDraft: true,
            options: [{ value: 'PROJECT', label: 'For project' }, { value: 'STOCK', label: 'For stock' }, { value: 'TOOL', label: 'For tool' }] },
          { key: 'documentDate', label: 'Document date', control: 'date', required: true, width: 'half' },
          { key: 'supplierId', label: 'Supplier', control: 'lookup', required: true, width: 'half', lookup: { entity: 'supplier' } },
          { key: 'projectId', label: 'Project', control: 'lookup', required: true, width: 'half', lookup: { entity: 'project' },
            visibleWhen: { field: 'orderType', equals: ['PROJECT'] } },
          // "Randomly you can't do PO for any item — only if you request"
          // (docs/video-notes/06 §8). Naming the request here is what lets
          // `Pull the open lines` resolve it, and what the
          // `order_without_request` control looks for.
          { key: 'materialRequestId', label: 'Against material request', control: 'lookup', width: 'half',
            lookup: { entity: 'material_request' },
            visibleWhen: { field: 'orderType', equals: ['PROJECT'] },
            hint: 'The site request this order answers. Its open lines can be pulled straight in.' },
          /*
            The quote being bought at, and the stock request being answered.

            Both types offered "Purchase order" as their next document and the
            order had no field pointing back at either, so awarding an RFQ and
            then buying at the awarded rate meant re-picking and re-typing
            every line — which is the payoff of the whole RFQ feature
            (docs/video-notes/06 §5).
          */
          // docs/24 §3.4 — the purchase executive's decision the order carries out.
          { key: 'purchaseRequisitionId', label: 'Against purchase requisition', control: 'lookup', width: 'half',
            lookup: { entity: 'purchase_requisition' },
            hint: 'The requisition this order buys for. Its open lines can be pulled straight in.' },
          { key: 'supplierQuotationId', label: 'Against quotation', control: 'lookup', width: 'half',
            lookup: { entity: 'supplier_quotation' },
            hint: "The quotation this order is placed at. Pull its lines to buy at the rates quoted." },
          { key: 'stockPurchaseRequestId', label: 'Against stock request', control: 'lookup', width: 'half',
            lookup: { entity: 'stock_purchase_request' },
            visibleWhen: { field: 'orderType', equals: ['STOCK'] },
            hint: 'The warehouse request this order replenishes.' },
          { key: 'warehouseId', label: 'Warehouse', control: 'lookup', width: 'half', lookup: { entity: 'warehouse' },
            hint: 'Where the goods land. A receipt note against this order creates stock here.' },
          { key: 'jobTypeId', label: 'Job type', control: 'lookup', width: 'half', lookup: { entity: 'job_type' } },
          { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', required: true, width: 'half', lookup: { entity: 'tax_unit' } },
          { key: 'purchaseExecutiveId', label: 'Purchase executive', control: 'lookup', width: 'half', lookup: { entity: 'employee' } },
          { key: 'supplierQuoteNo', label: "Supplier's quote no", control: 'text', width: 'half' },
          { key: 'paymentTerms', label: 'Payment terms', control: 'text', width: 'half' },
          { key: 'creditPeriodDays', label: 'Credit period (days)', control: 'number', width: 'half' },
          { key: 'deliveryDate', label: 'Delivery date', control: 'date', width: 'half',
            hint: 'Once this has passed with nothing received, the order joins the overdue work list.' },
          { key: 'interstate', label: 'Interstate (IGST)?', control: 'checkbox', width: 'half',
            hint: 'Set automatically from the supplier and tax unit states. Change it only if you are sure.' },
          { key: 'remarks', label: 'Remarks', control: 'textarea', width: 'full' },
        ],
      },
      {
        key: 'delivery', n: '02', title: 'Delivery information',
        fields: [
          { key: 'deliveryAddressId', label: 'Deliver to', control: 'lookup', required: true, width: 'half', lookup: { entity: 'address' } },
          { key: 'billingAddressId', label: 'Bill to', control: 'lookup', width: 'half', lookup: { entity: 'address' } },
          { key: 'supplierAddressId', label: "Supplier's address", control: 'lookup', width: 'half', lookup: { entity: 'address' } },
          { key: 'contactId', label: 'Site contact', control: 'lookup', width: 'half', lookup: { entity: 'contact' } },
          { key: 'supplierContactId', label: "Supplier's contact", control: 'lookup', width: 'half', lookup: { entity: 'contact' } },
          // docs/06 §4 — who the goods are consigned to, which is not always
          // the company buying them, and is what the transporter reads.
          { key: 'consignee', label: 'Consignee', control: 'text', width: 'half',
            hint: 'Who the delivery is addressed to, when that is not this company.' },
          { key: 'deliveryCoordinator', label: 'Delivery coordinator', control: 'text', width: 'half' },
          { key: 'deliveryContactNo', label: 'Delivery contact no', control: 'text', width: 'half' },
          { key: 'notesToSupplier', label: 'Notes to the supplier', control: 'textarea', width: 'full' },
        ],
      },
      currencySection(),
      termsSection(),
    ],
  },

  /*
    Built from the shared priced grid rather than written out by hand.

    Written out by hand it silently missed `isBillable` when that was added to
    every other grid, and the purchase order is the one step in the chain where
    the classification is invisible and cannot be corrected — a line typed in
    or imported from Excel defaulted to billable with nobody able to see it.
  */
  lines: {
    ...pricedLines('Materials', '03'),
    sources: [
      { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' },
      { key: 'request', label: 'Pick from material requests', source: 'request' },
      { key: 'requisition', label: 'Pick from purchase requisitions', source: 'requisition' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },

  grids: [chargesGrid(), adjustmentsGrid()],

  columns: [
    { key: 'documentNo', label: 'Document no', type: 'docno', width: 152 },
    { key: 'documentDate', label: 'Date', type: 'date', width: 128 },
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    { key: 'projectName', label: 'Project', type: 'text' },
    { key: 'jobTypeName', label: 'Job type', type: 'text', width: 205 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210 },
    { key: 'total', label: 'Total', type: 'money', width: 156, align: 'right' },
    ...fcyColumns(),
  ],

  filters: [
    { key: 'documentNo', label: 'Document no', control: 'text' },
    { key: 'documentDate', label: 'Document date', control: 'dateRange' },
    { key: 'documentStatus', label: 'Status', control: 'status' },
    { key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } },
    { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } },
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    { key: 'submittedById', label: 'Submitted by', control: 'lookup', lookup: { entity: 'employee' } },
    { key: 'fulfilment', label: 'Progress', control: 'select', options: [
      { value: 'OPEN', label: 'Anything still open' },
      { value: 'PARTIAL', label: 'Partly received' },
      { value: 'COMPLETE', label: 'Fully received' },
    ] },
  ],

  actions: [...SYNCED_ACTIONS, 'preClose', 'undoPreClose'],
  rules: [
    'order_without_request', 'order_without_requisition', 'non_tendered_order', 'rate_exceeds_estimation',
    'excess_purchase_quantity', 'brand_mismatch', 'purchase_plan_required',
    'quotation_not_awarded', 'supplier_not_approved',
  ],
  allocatesFrom: ['material_request', 'purchase_requisition', 'supplier_quotation', 'stock_purchase_request'],
  createsNext: [
    { docType: 'goods_receipt', label: 'Receipt note (GRN)', whenStatus: 'APPROVED' },
    { docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' },
  ],
} as const)
